import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Pencil, Trash2, Share2, Frame } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useArt, useWorks, patchWork, fmtDate } from "../../lib/art";
import { ArtShell } from "../../components/art/ArtShell";
import { Framed, FramePicker } from "../../components/art/Framed";
import { ShareWorkDialog } from "../../components/art/ShareWorkDialog";
import { ParentGate } from "../../components/ParentGate";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";

const EditDialog = ({ w, frames, full, onClose, onSaved }) => {
  const [f, setF] = useState({ title: w.title, says: w.says || "", frame: w.frame });
  const save = async () => {
    try { onSaved(await patchWork(w.work_id, f)); onClose(); } catch (e) { toast.error(e?.response?.data?.detail || "儲存失敗"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-[2rem] max-w-lg bg-[#FFFBF3]" data-testid="art-edit-dialog">
        <DialogTitle className="text-2xl font-black">修改作品</DialogTitle>
        <Input data-testid="art-edit-title" value={f.title} maxLength={40} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-12 text-lg rounded-xl" />
        <Input data-testid="art-edit-says" value={f.says} maxLength={80} placeholder="我想說的話" onChange={(e) => setF({ ...f, says: e.target.value })} className="h-12 text-lg rounded-xl" />
        <FramePicker frames={frames} value={f.frame} onChange={(frame) => setF({ ...f, frame })} full={full} />
        <button data-testid="art-edit-save" onClick={save} className="h-12 rounded-full bg-[#E2683C] text-white font-black text-lg">儲存</button>
      </DialogContent>
    </Dialog>
  );
};

const IconBtn = ({ testid, onClick, children, active, label }) => (
  <button data-testid={testid} onClick={onClick} title={label} className={`h-11 px-3 rounded-full flex items-center gap-1 text-sm font-bold ${active ? "bg-pink-100 text-pink-600" : "bg-stone-100 text-stone-600"}`}>{children}</button>
);

export default function ArtWorks() {
  const nav = useNavigate();
  const [nickname, setNickname] = useState("");
  const [meta] = useArt();
  const [works, , setWorks, childId] = useWorks();
  useEffect(() => { api.get("/children").then((r) => setNickname(r.data.find((c) => c.child_id === childId)?.nickname || "")); }, [childId]);
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const [gate, setGate] = useState(null);
  const [share, setShare] = useState(null);
  const upd = (w) => setWorks((ws) => ws.map((x) => (x.work_id === w.work_id ? w : x)));
  const toggle = async (w, key) => {
    try { upd(await patchWork(w.work_id, { [key]: !w[key] })); } catch (e) { toast.error(e?.response?.data?.detail || "失敗了", e?.response?.status === 402 ? { action: { label: "查看方案", onClick: () => nav("/pricing") } } : undefined); }
  };
  const remove = async (w) => {
    if (!window.confirm(`要刪除「${w.title}」嗎？`)) return;
    await api.delete(`/art/works/${w.work_id}`);
    setWorks((ws) => ws.filter((x) => x.work_id !== w.work_id));
  };
  const list = (works || []).filter((w) => filter === "all" || w.favorite);
  return (
    <ArtShell title="我的作品集" testid="art-works-page"
      right={meta?.works_limit && <span className="text-stone-500 text-sm sm:text-base" data-testid="art-works-limit">已保存 {works?.length ?? 0} / {meta.works_limit}</span>}>
      <div className="flex gap-2 mb-6">
        {[["all", "全部作品"], ["fav", "我的收藏"]].map(([k, l]) => (
          <button key={k} data-testid={`art-works-filter-${k}`} onClick={() => setFilter(k)} className={`h-12 px-5 rounded-full font-bold text-lg ${filter === k ? "bg-[#E2683C] text-white" : "bg-white text-stone-600 border-2 border-[#f3d9b8]"}`}>{l}</button>
        ))}
      </div>
      {works && !list.length && <div className="text-center py-20 text-stone-500 text-xl" data-testid="art-works-empty">還沒有作品，去畫一幅吧！</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map((w) => (
          <div key={w.work_id} className="bg-white/80 rounded-[2rem] p-4 border-2 border-[#f6e2c8]" data-testid={`art-work-${w.work_id}`}>
            <Framed src={w.image_url} frame={w.frame} />
            <div className="mt-3 text-xl font-black text-stone-800 truncate" data-testid={`art-work-title-${w.work_id}`}>{w.title}</div>
            <div className="text-sm text-stone-500">{fmtDate(w.created_at)}</div>
            {w.says && <div className="mt-1 text-stone-700">「{w.says}」</div>}
            <div className="flex flex-wrap gap-2 mt-3">
              <IconBtn testid={`art-work-fav-${w.work_id}`} active={w.favorite} onClick={() => toggle(w, "favorite")} label="收藏"><Heart size={18} fill={w.favorite ? "currentColor" : "none"} /></IconBtn>
              <IconBtn testid={`art-work-edit-${w.work_id}`} onClick={() => setEdit(w)} label="修改"><Pencil size={18} /></IconBtn>
              <IconBtn testid={`art-work-gallery-${w.work_id}`} active={w.in_gallery} onClick={() => toggle(w, "in_gallery")} label="放進小畫廊"><Frame size={18} />{w.in_gallery ? "在畫廊" : `放進畫廊${meta?.full ? "" : " 👑"}`}</IconBtn>
              <IconBtn testid={`art-work-share-${w.work_id}`} onClick={() => (meta?.full ? setGate(w) : toast("分享是 Premium 功能", { action: { label: "查看方案", onClick: () => nav("/pricing") } }))} label="家長分享"><Share2 size={18} />家長分享</IconBtn>
              <IconBtn testid={`art-work-delete-${w.work_id}`} onClick={() => remove(w)} label="刪除"><Trash2 size={18} /></IconBtn>
            </div>
          </div>
        ))}
      </div>
      {edit && meta && <EditDialog w={edit} frames={meta.frames} full={meta.full} onClose={() => setEdit(null)} onSaved={upd} />}
      <ParentGate open={!!gate} onOpenChange={(o) => !o && setGate(null)} onPass={() => { setShare(gate); setGate(null); }} />
      {share && <ShareWorkDialog work={share} nickname={nickname} onClose={() => setShare(null)} />}
    </ArtShell>
  );
}
